/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faCamera, faUpload } from "@fortawesome/pro-regular-svg-icons";
import { useMemo, useState } from "react";
import ImageUploading from "react-images-uploading";
import { Button } from "../../../../Helper/Button";

type ImageUploaderProps = {
    onSaveImage(image: string, val?: boolean): void;
    images?: any[];
    isLoading?: boolean;
    onOpenCamera: () => void;
}

const MAX_IMAGES = 25;

export function ImageUploader({ onSaveImage, onOpenCamera, ...props }: ImageUploaderProps) {
    const [localIsLoading, setIsLoading] = useState(false);

    const images = useMemo(() => (props?.images ?? []).map((image) => ({
        data_url: image.url_webp || image.url,
        id: image.image_id || image.id,
        original: image
    })), [props?.images]);

    const isLoading = useMemo(() => props.isLoading || localIsLoading, [props.isLoading, localIsLoading]);

    const onChange = (imageList) => {
        setIsLoading(true);

        const newImages = imageList.filter((i) => !i.id);
        // also keep a local loading state to prevent flickering of the loading state
        const timeBetweenUploads = 1000;
        setTimeout(() => setIsLoading(false), newImages.length * timeBetweenUploads + 100);

        newImages.forEach((img, index) => {
            const capturedImage = img.data_url;

            setTimeout(() => {
                onSaveImage(capturedImage, false);
            }, index * timeBetweenUploads);
        });
    };

    return (
        <ImageUploading
            multiple
            value={images}
            onChange={onChange}
            maxNumber={MAX_IMAGES}
            dataURLKey="data_url"
        >
            {({
                onImageUpload,
                isDragging,
                dragProps,
            }) => (
                <div className="upload__image-wrapper">
                    <div
                        css={css`width: 100%; padding: 5rem 2rem; border: 2px dashed ${(isDragging ? "var(--bs-success)" : "#ddd")};`}
                        className="text-center"
                        {...dragProps}
                    >
                        <Button className="mb-3 mx-2" color="primary" disabled={isLoading} onClick={onImageUpload} icon={faUpload} isLoading={isLoading}>
                            Upload images
                        </Button>

                        <Button className="mb-3 mx-2" color="primary" disabled={isLoading} onClick={onOpenCamera} icon={faCamera} isLoading={isLoading}>
                            Use camera
                        </Button>
                    </div>
                </div>
            )}
        </ImageUploading>
    );
}
